/**
 * VideoPanel - Displays a list of videos for a selected location
 */
export class VideoPanel {
  constructor(containerId) {
    this.container = document.getElementById(containerId);
    if (!this.container) {
      throw new Error(`Container element with id "${containerId}" not found`);
    }

    this.currentLocation = null;
    this.currentVideos = [];
    this.setupEventListeners();
  }

  setupEventListeners() {
    // Close button handler
    const closeButton = this.container.querySelector('.close-button');
    if (closeButton) {
      closeButton.addEventListener('click', () => this.hide());
    }
  }

  /**
   * Show video panel with list of videos for a location
   * @param {string} locationName - Name of the location
   * @param {Array} videos - Array of video items {id, year, locations, metadata}
   * @param {number} startYear - Start of time window
   * @param {number} endYear - End of time window
   */
  show(locationName, videos, startYear, endYear) {
    this.currentLocation = locationName;
    this.currentVideos = videos;

    // Update header
    const header = this.container.querySelector('#location-name');
    if (header) {
      header.textContent = locationName;
    }

    // Update subtitle with count and time window
    const subtitle = this.container.querySelector('.video-panel-subtitle');
    if (subtitle) {
      const count = videos.length;
      const videoText = count === 1 ? 'video' : 'videos';
      subtitle.textContent = `${count} ${videoText} (${startYear}–${endYear})`;
    }

    // Render video list
    this.renderVideoList(videos);

    // Show panel
    this.container.style.display = 'block';
  }

  /**
   * Hide the video panel
   */
  hide() {
    this.container.style.display = 'none';
    this.currentLocation = null;
    this.currentVideos = [];
  }

  /**
   * Render the list of videos grouped by year
   * @param {Array} videos - Array of video items
   */
  renderVideoList(videos) {
    const listContainer = this.container.querySelector('.video-list');
    if (!listContainer) return;

    // Clear existing content
    listContainer.innerHTML = '';

    if (videos.length === 0) {
      listContainer.innerHTML = '<div class="no-videos">No videos found in this time period</div>';
      return;
    }

    // Group videos by year
    const videosByYear = this.groupByYear(videos);

    // Sort years in descending order
    const sortedYears = Object.keys(videosByYear).sort((a, b) => b - a);

    // Create year groups
    sortedYears.forEach(year => {
      const yearGroup = document.createElement('div');
      yearGroup.className = 'video-year-group';

      // Year header
      const yearHeader = document.createElement('div');
      yearHeader.className = 'video-year-header';
      yearHeader.textContent = year === 'null' ? 'Unknown year' : year;
      yearGroup.appendChild(yearHeader);

      // Video items
      const items = videosByYear[year];
      items.forEach(video => {
        const videoItem = document.createElement('div');
        videoItem.className = 'video-item';

        // Add metadata badge
        const badge = document.createElement('span');
        badge.className = `metadata-badge metadata-${video.metadata}`;
        badge.textContent = video.metadata === 'metadata1' ? 'M1' : 'M2';

        const videoId = document.createElement('span');
        videoId.className = 'video-id';
        videoId.textContent = video.id;

        videoItem.appendChild(badge);
        videoItem.appendChild(videoId);
        yearGroup.appendChild(videoItem);
      });

      listContainer.appendChild(yearGroup);
    });
  }

  /**
   * Group videos by year
   * @param {Array} videos - Array of video items
   * @returns {Object} Videos grouped by year
   */
  groupByYear(videos) {
    const grouped = {};

    videos.forEach(video => {
      const year = video.year || 'null';
      if (!grouped[year]) {
        grouped[year] = [];
      }
      grouped[year].push(video);
    });

    return grouped;
  }

  /**
   * Update the video list (convenience method)
   */
  update(locationName, videos, startYear, endYear) {
    this.show(locationName, videos, startYear, endYear);
  }
}
